import { useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Sheet, SheetContent, SheetTitle } from '@/shared/components/ui/sheet'
import { PendingInvoiceBanner } from '@/features/organization/components/PendingInvoiceBanner'
import { AnnouncementBar } from '@/features/marketing/components/AnnouncementBar'
import { Sidebar } from './sidebar'
import { Topbar } from './Topbar'

/**
 * Layout autenticado responsive (mobile-first).
 *
 * - En `<md` el sidebar vive en un `<Sheet>` lateral que se abre desde la
 *   hamburguesa de la topbar. El cierre al navegar es derivado del state: el
 *   sheet sólo está abierto mientras `openedAt === location.pathname`.
 * - En `≥md` el mismo `<Sidebar />` se renderiza como un `<aside>` sticky a la
 *   izquierda. Sin lógica de viewport en JS — todo es Tailwind.
 *
 * 🚨 **La ALTURA DE VENTANA la fija UNA sola pieza, y no es el `aside`**
 * (goal `portal-correcciones 9`). La columna exterior ya es `min-h-screen`; si
 * además el `aside` se declara `h-screen`, la carcasa mide **cintillo + 100vh**
 * en cuanto el CMS enciende el `AnnouncementBar` — medido: 940 px en una
 * ventana de 900, o sea una barra de scroll vertical de exactamente los 40 px
 * del cintillo en una página cuyo contenido cabía de sobra.
 *
 * El reparto correcto, que es el que hay abajo:
 *   · el `aside` **se estira** con la fila (no fija altura propia) y pinta el
 *     fondo de la columna entera, así que nunca deja franja;
 *   · el cajón `sticky` de dentro mide **lo que la columna** (`h-full`)
 *     **acotado a una ventana** (`max-h-screen`): con contenido corto vale
 *     860 px y no desborda; con página larga vale 100vh y se queda pegado.
 *
 * 🚨 **El envoltorio `absolute inset-0` NO es decoración: es lo que rompe la
 * circularidad.** Medido al ejecutar el goal: con el cajón `sticky` colgando
 * directo del `aside`, el `aside` toma su altura DEL HIJO (900) y arrastra a la
 * fila a 900, con lo que `max-h-full` resuelve contra 900 y no acota nada — el
 * scroll de 40 px seguía ahí, con el arreglo puesto. Un hijo absoluto no aporta
 * altura: la fila vuelve a medir lo que le toca (`flex-1` = 100vh − cintillo) y
 * solo entonces `h-full` tiene contra qué resolver.
 *
 * ⚠️ No devolver `h-screen` al `aside` «porque el menú tiene que llenar la
 * pantalla», ni quitar el `absolute inset-0` «porque sobra un div»: cada una
 * por su lado reintroduce el scroll fantasma. Hay candado en
 * `AuthenticatedLayout.test.tsx` y medición en
 * `local/e2e/portal-correcciones-9/pase-visual.mjs`.
 */
export function AuthenticatedLayout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const mobileOpen = openedAt === location.pathname

  const handleOpenChange = (next: boolean) => {
    setOpenedAt(next ? location.pathname : null)
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Cintillo de anuncio: también visible dentro de la administración. */}
      <AnnouncementBar />
      <div className="flex min-h-0 flex-1">
        <aside className="relative hidden w-[248px] shrink-0 bg-sidebar md:block">
          <div className="absolute inset-0">
            <div className="sticky top-0 h-full max-h-screen">
              <Sidebar />
            </div>
          </div>
        </aside>

        <Sheet open={mobileOpen} onOpenChange={handleOpenChange}>
          <SheetContent
            side="left"
            className="w-[280px] border-r-0 bg-sidebar p-0 text-[#d1d5db]"
            showClose={false}
          >
            <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
            <Sidebar />
          </SheetContent>
        </Sheet>

        <main className="flex min-w-0 flex-1 flex-col">
          <Topbar onMenuClick={() => handleOpenChange(true)} />
          <PendingInvoiceBanner />
          <div className="flex-1 p-4 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  )
}
